import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AUTH_TOKEN_KEY, AUTH_USER_KEY, authService, clearStoredAuthToken, getStoredAuthToken } from '../api/api';
import { canAccessPath, canManageAccess, defaultPathForUser, hasFullAccess, hasModuleAccess, normalizeRole, normalizeModulePermissions, roleLabel } from './roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(getStoredAuthToken);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const logout = useCallback(() => {
    clearStoredAuthToken();
    setToken(null);
    setUser(null);
  }, []);

  const saveSession = useCallback((nextToken, nextUser, rememberMe = false) => {
    clearStoredAuthToken();
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(AUTH_TOKEN_KEY, nextToken);
    storage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const login = useCallback(async (payload, options = {}) => {
    const res = await authService.login({ ...payload, rememberMe: options.rememberMe === true });
    saveSession(res.data.token, res.data.user, options.rememberMe === true);
    return res.data.user;
  }, [saveSession]);

  const signup = useCallback(async (payload) => {
    const res = await authService.signup(payload);
    saveSession(res.data.token, res.data.user, false);
    return res.data.user;
  }, [saveSession]);

  const refreshMe = useCallback(async (options = {}) => {
    const quiet = options?.quiet === true;
    const currentToken = getStoredAuthToken();
    if (!currentToken) {
      setLoading(false);
      setUser(null);
      setToken(null);
      return;
    }

    try {
      if (!quiet) setLoading(true);
      const res = await authService.me();
      if (getStoredAuthToken() !== currentToken) return;
      const storage = sessionStorage.getItem(AUTH_TOKEN_KEY) ? sessionStorage : localStorage;
      storage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
      if (res.data.token) storage.setItem(AUTH_TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      setToken(res.data.token || currentToken);
    } catch (error) {
      if (getStoredAuthToken() !== currentToken) return;
      if ([401, 403].includes(error?.response?.status)) {
        logout();
      } else {
        try {
          const storage = sessionStorage.getItem(AUTH_TOKEN_KEY) ? sessionStorage : localStorage;
          const cachedUser = JSON.parse(storage.getItem(AUTH_USER_KEY) || 'null');
          if (cachedUser) setUser(cachedUser);
        } catch {
          setUser(null);
        }
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('authExpired', handler);
    return () => window.removeEventListener('authExpired', handler);
  }, [logout]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshQuietly = () => refreshMe({ quiet: true });
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshQuietly();
    };

    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('usersUpdated', refreshQuietly);
    window.addEventListener('dataUpdated', refreshQuietly);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const refreshInterval = window.setInterval(refreshQuietly, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('usersUpdated', refreshQuietly);
      window.removeEventListener('dataUpdated', refreshQuietly);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.clearInterval(refreshInterval);
    };
  }, [refreshMe, token]);

  const value = useMemo(() => ({
    token,
    user,
    loading,
    isAuthenticated: Boolean(token && user),
    role: normalizeRole(user?.role),
    isAdmin: normalizeRole(user?.role) === 'global_admin',
    hasFullAccess: hasFullAccess(user?.role),
    canManageAccess: canManageAccess(user?.role),
    roleLabel: roleLabel(user?.role),
    modulePermissions: normalizeModulePermissions(user?.role, user?.module_permissions),
    hasModuleAccess: (moduleName) => hasModuleAccess(user, moduleName),
    canAccessPath: (pathname) => canAccessPath(user, pathname),
    defaultPath: defaultPathForUser(user),
    login,
    signup,
    logout,
    refreshMe,
  }), [loading, login, logout, refreshMe, signup, token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
